import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";
import axios from "axios";

/**
 * Where a reproduction run puts its marking, and where the substrate lies
 * under the laser.
 *
 * Draws the laser's marking field, the substrate at its offset, the part of
 * it the laser can reach (substrate n field) and the box of the instance,
 * with the source drawn inside it. Both the substrate and the box are
 * dragged with the pointer or typed; the geometry comes from the server
 * (POST api/reproduction/marking/layout), the same computation the run
 * itself uses.
 *
 * Centering is a choice: on the reachable part ("Centre in field", which
 * always fits) or on the whole substrate ("Centre on substrate", which may
 * land where the laser cannot reach - the run says so). Both are resolved
 * by the run, so a centered marking is centered on its real bounding box
 * rather than on this nominal one. Corner and dragging make the position
 * custom.
 *
 * The laser's marking field is the hard limit: the box cannot be dragged
 * out of it, and a typed position outside it is refused (error). Past the
 * substrate is allowed with a warning.
 *
 * A refused layout (e.g. a typed substrate offset that puts the plate
 * outside the field) keeps the last drawn one and shows the reason under
 * the inputs, so the value can be corrected; without any layout yet the
 * substrate inputs stand alone next to the reason.
 *
 * Props:
 *   reproductionId
 *   params                      : the per-run parameters of the Generate (size etc.)
 *   positionMode                : "center" | "center_substrate" | "custom"
 *   position                    : { x, y } in mm, or null while none was typed
 *   onPositionChange(position)  : a position was dragged or typed (implies "custom")
 *   onModeChange(mode)          : the mode was switched by a centre button or a drag
 *   plateOffset                 : { x, y } in mm of the substrate's corner
 *   onPlateOffsetChange(offset)
 *   onLayoutChange(layout|null) : the server's layout, null while it could not be computed
 *   contentUrl                  : preview of the source drawn inside the box
 *   subject                     : "logo" | "image" | "shape", for the summary line
 */
export const LAYOUT_URL = "api/reproduction/marking/layout";
const CANVAS_WIDTH_PX = 560;
const MARGIN_MM = 8;
// The substrate may hang out of the field, but this much of it has to stay
// reachable - a plate fully outside has no layout at all.
const MIN_OVERLAP_MM = 1;

const roundMm = (value) => Math.round(value * 10) / 10;
const fmt = (value) => (Number.isFinite(value) ? String(Math.round(value * 10) / 10) : "?");
const clamp = (value, low, high) => Math.min(Math.max(value, low), high);

export default function ReproductionPlacement({
  reproductionId,
  params,
  positionMode,
  position,
  onPositionChange,
  onModeChange,
  plateOffset,
  onPlateOffsetChange,
  onLayoutChange,
  contentUrl,
  subject = "marking",
}) {
  const [layout, setLayout] = useState(null);
  const [error, setError] = useState("");
  const dragRef = useRef(null);
  const requestRef = useRef(0);
  // The parameters are rebuilt on every render; only their values may
  // trigger a request.
  const paramsKey = JSON.stringify(params || {});

  useEffect(() => {
    if (!reproductionId) return undefined;
    const timer = setTimeout(async () => {
      const id = ++requestRef.current;
      try {
        const res = await axios.post(LAYOUT_URL, {
          reproduction_id: reproductionId,
          ...JSON.parse(paramsKey),
        });
        if (id !== requestRef.current) return;
        const next = res?.data?.layout || null;
        setLayout(next);
        setError("");
        if (onLayoutChange) onLayoutChange(next);
      } catch (err) {
        if (id !== requestRef.current) return;
        // The last layout stays drawn: the inputs must remain to correct the value.
        setError(err?.response?.data?.error || "The position could not be computed.");
        if (onLayoutChange) onLayoutChange(null);
      }
    }, 120);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reproductionId, paramsKey]);

  // Both drags follow the pointer before the server answers: everything is
  // drawn shifted by the distance to the values it was computed for. A
  // centered marking rides along with its substrate.
  const isCustom = positionMode === "custom";
  const boxShift = layout && isCustom && position
    ? { x: position.x - layout.box_mm.x, y: position.y - layout.box_mm.y }
    : { x: 0, y: 0 };
  const plateShift = layout && plateOffset
    ? { x: plateOffset.x - layout.plate_mm.x, y: plateOffset.y - layout.plate_mm.y }
    : { x: 0, y: 0 };
  const shift = isCustom
    ? boxShift
    : { x: boxShift.x + plateShift.x, y: boxShift.y + plateShift.y };

  // The drawn extent follows the dragged substrate; a refused offset does
  // not stretch the canvas (the field stays readable, the plate is clipped).
  const world = useMemo(() => {
    if (!layout) return null;
    const { field_mm: field, plate_mm: plate } = layout;
    const follow = plateOffset && !error;
    const left = plate.x + (follow ? plateOffset.x - plate.x : 0);
    const top = plate.y + (follow ? plateOffset.y - plate.y : 0);
    const minX = Math.min(0, left) - MARGIN_MM;
    const minY = Math.min(0, top) - MARGIN_MM;
    const maxX = Math.max(field.width, left + plate.width) + MARGIN_MM;
    const maxY = Math.max(field.height, top + plate.height) + MARGIN_MM;
    return { minX, minY, width: maxX - minX, height: maxY - minY };
  }, [layout, plateOffset, error]);

  const plateAt = plateOffset || (layout ? { x: layout.plate_mm.x, y: layout.plate_mm.y } : { x: 0, y: 0 });
  const setPlateAxis = (axis) => (event) => {
    const value = parseFloat(event.target.value);
    if (Number.isFinite(value)) onPlateOffsetChange({ ...plateAt, [axis]: value });
  };
  const substrateRow = (
    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
      <TextField
        label="Substrate x (mm)"
        type="number"
        size="small"
        inputProps={{ step: 0.5 }}
        value={roundMm(plateAt.x)}
        onChange={setPlateAxis("x")}
        sx={{ width: 150 }}
      />
      <TextField
        label="Substrate y (mm)"
        type="number"
        size="small"
        inputProps={{ step: 0.5 }}
        value={roundMm(plateAt.y)}
        onChange={setPlateAxis("y")}
        sx={{ width: 150 }}
      />
      <Button
        variant="outlined"
        size="small"
        onClick={() => onPlateOffsetChange({ x: 0, y: 0 })}
        sx={{ textTransform: "none" }}
      >
        Field corner
      </Button>
    </Stack>
  );

  if (!layout || !world) {
    if (!error) return null;
    return (
      <Stack spacing={1.5} sx={{ width: "100%" }}>
        {substrateRow}
        <Alert severity="error">{error}</Alert>
      </Stack>
    );
  }

  const { field_mm: field, plate_mm: plate, usable_mm: usable, box_mm: box } = layout;
  const scale = CANVAS_WIDTH_PX / world.width;
  const canvasHeight = world.height * scale;
  const px = (mm) => mm * scale;
  const toX = (mm) => px(mm - world.minX);
  const toY = (mm) => px(mm - world.minY);
  const bx = (mm) => toX(mm + shift.x);
  const by = (mm) => toY(mm + shift.y);
  const sx = (mm) => toX(mm + plateShift.x);
  const sy = (mm) => toY(mm + plateShift.y);

  // Positions that keep the box inside the laser's marking field (the
  // substrate may be left, with a warning).
  const bounds = {
    minX: 0,
    minY: 0,
    maxX: Math.max(0, field.width - box.width),
    maxY: Math.max(0, field.height - box.height),
  };
  // Offsets that keep at least a corner of the substrate reachable.
  const plateBounds = {
    minX: MIN_OVERLAP_MM - plate.width,
    minY: MIN_OVERLAP_MM - plate.height,
    maxX: field.width - MIN_OVERLAP_MM,
    maxY: field.height - MIN_OVERLAP_MM,
  };
  const current = isCustom && position ? position : { x: box.x, y: box.y };

  const moveTo = (next) => {
    if (!isCustom && onModeChange) onModeChange("custom");
    onPositionChange(next);
  };

  const startDrag = (kind, origin) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = { kind, startX: event.clientX, startY: event.clientY, origin: { ...origin } };
  };
  const onPointerMove = (event) => {
    if (!dragRef.current) return;
    const dx = (event.clientX - dragRef.current.startX) / scale;
    const dy = (event.clientY - dragRef.current.startY) / scale;
    const { kind, origin } = dragRef.current;
    if (kind === "plate") {
      onPlateOffsetChange({
        x: roundMm(clamp(origin.x + dx, plateBounds.minX, plateBounds.maxX)),
        y: roundMm(clamp(origin.y + dy, plateBounds.minY, plateBounds.maxY)),
      });
      return;
    }
    moveTo({
      x: roundMm(clamp(origin.x + dx, bounds.minX, bounds.maxX)),
      y: roundMm(clamp(origin.y + dy, bounds.minY, bounds.maxY)),
    });
  };
  const onPointerUp = (event) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const setAxis = (axis) => (event) => {
    const value = parseFloat(event.target.value);
    if (Number.isFinite(value)) moveTo({ ...current, [axis]: value });
  };
  const toCorner = () => moveTo({ ...layout.corner_position });
  const centreOn = (mode) => () => {
    if (onModeChange) onModeChange(mode);
    onPositionChange(null);
  };

  const fits = layout.fits;
  const warning = fits ? layout.warning : null;
  const centreButton = (mode, label) => (
    <Button
      variant={positionMode === mode ? "contained" : "outlined"}
      size="small"
      disableElevation
      onClick={centreOn(mode)}
      sx={{ textTransform: "none" }}
    >
      {label}
    </Button>
  );

  return (
    <Stack spacing={1.5} sx={{ width: "100%" }}>
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <svg
          data-testid="reproduction-placement-canvas"
          width={CANVAS_WIDTH_PX}
          height={canvasHeight}
          viewBox={`0 0 ${CANVAS_WIDTH_PX} ${canvasHeight}`}
          style={{ display: "block", touchAction: "none", userSelect: "none" }}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {/* marking field */}
          <rect
            x={toX(0)}
            y={toY(0)}
            width={px(field.width)}
            height={px(field.height)}
            fill="#f4f6f8"
            stroke="#5b6b7a"
            strokeWidth={1.5}
            strokeDasharray="6 4"
          />
          <text x={toX(0)} y={toY(field.height) + 14} fontSize={12} fill="#5b6b7a">
            {`Marking field ${fmt(field.width)} × ${fmt(field.height)} mm`}
          </text>
          {/* the substrate (draggable) with the part the laser reaches */}
          <g
            data-testid="reproduction-placement-plate"
            style={{ cursor: "grab" }}
            onPointerDown={startDrag("plate", plateAt)}
          >
            <rect
              x={sx(plate.x)}
              y={sy(plate.y)}
              width={px(plate.width)}
              height={px(plate.height)}
              fill="transparent"
            />
            <rect
              x={sx(usable.x)}
              y={sy(usable.y)}
              width={px(usable.width)}
              height={px(usable.height)}
              fill="#dbeafe"
            />
            <rect
              x={sx(plate.x)}
              y={sy(plate.y)}
              width={px(plate.width)}
              height={px(plate.height)}
              fill="none"
              stroke="#4C7EFF"
              strokeWidth={2}
            />
            <text x={sx(plate.x) + 6} y={sy(plate.y) - 5} fontSize={12} fill="#4C7EFF">
              {`Substrate ${fmt(plate.width)} × ${fmt(plate.height)} mm`}
            </text>
          </g>
          {/* the instance (draggable), on top of the substrate */}
          <g
            data-testid="reproduction-placement-box"
            style={{ cursor: "grab" }}
            onPointerDown={startDrag("box", current)}
          >
            <rect
              x={bx(box.x)}
              y={by(box.y)}
              width={px(box.width)}
              height={px(box.height)}
              fill="rgba(255, 255, 255, 0.85)"
              stroke={!fits ? "#d32f2f" : warning ? "#ed6c02" : "#1f2937"}
              strokeWidth={fits && !warning ? 1 : 2}
            />
            {contentUrl ? (
              <image
                href={contentUrl}
                x={bx(box.x)}
                y={by(box.y)}
                width={px(box.width)}
                height={px(box.height)}
                preserveAspectRatio="none"
                opacity={0.85}
              />
            ) : null}
          </g>
          <circle cx={toX(0)} cy={toY(0)} r={3} fill="#5b6b7a" />
        </svg>
      </Box>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          label="Position x (mm)"
          type="number"
          size="small"
          inputProps={{ step: 0.5 }}
          value={roundMm(current.x)}
          onChange={setAxis("x")}
          sx={{ width: 150 }}
        />
        <TextField
          label="Position y (mm)"
          type="number"
          size="small"
          inputProps={{ step: 0.5 }}
          value={roundMm(current.y)}
          onChange={setAxis("y")}
          sx={{ width: 150 }}
        />
        <Button variant="outlined" size="small" onClick={toCorner} sx={{ textTransform: "none" }}>
          Corner
        </Button>
        {centreButton("center", "Centre in field")}
        {centreButton("center_substrate", "Centre on substrate")}
      </Stack>

      {substrateRow}

      <Typography variant="body2" color="text.secondary" data-testid="reproduction-placement-summary">
        {`The ${subject} is ${fmt(box.width)} × ${fmt(box.height)} mm; the laser reaches `
          + `${fmt(usable.width)} × ${fmt(usable.height)} mm of the substrate. `
          + (positionMode === "center"
            ? "It is centered there."
            : positionMode === "center_substrate"
              ? "It is centered on the whole substrate."
              : "Position: its top-left corner from the field's top-left corner.")}
      </Typography>
      {error
        ? <Alert severity="error">{error}</Alert>
        : (!fits && layout.error ? <Alert severity="error">{layout.error}</Alert> : null)}
      {!error && warning ? <Alert severity="warning">{warning}</Alert> : null}
    </Stack>
  );
}
